import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingChat from "./components/FloatingChat";
import LandingHome from "./pages/LandingHome";
import AutoLifestyleGPS from "./pages/AutoLifestyleGPS";
import AutoLifestyleRoute from "./pages/AutoLifestyleRoute";
import DrivenServices from "./pages/DrivenServices";
import DrivenOccasions from "./pages/DrivenOccasions";
import OccasionPage from "./pages/OccasionPage";
import FleetRateCard from "./pages/FleetRateCard";
import DrivingServices from "./pages/DrivingServices";
import Membership from "./pages/Membership";
import Contact from "./pages/Contact";
import Consultation from "./pages/Consultation";
import Pricing from "./pages/Pricing";
import Corporate from "./pages/Corporate";
import Partners from "./pages/Partners";
import FleetGallery from "./pages/FleetGallery";
import ClientCRM from "./pages/ClientCRM";
import ConsultingPipeline from "./pages/ConsultingPipeline";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Search from "./pages/Search";
import VehicleDetail from "./pages/VehicleDetail";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import BookingNew from "./pages/BookingNew";
import {
  BookingRequestPage, ChauffeurRequestPage, AirportTransferPage,
  EventBookingPage, OwnerOnboardingPage, BrokerSubmissionPage,
} from "./pages/IntakePages";
import ServicesHub from "./pages/ServicesHub";
import { BrokerLoginPage, OwnerLoginPage } from "./pages/PortalLoginPages";
import BrokerDashboard from "./pages/BrokerDashboard";
import OwnerDashboard from "./pages/OwnerDashboard";
import PartnerPortal from "./pages/PartnerPortal";
import OSShell from "./os/OSShell";
import OSKit from "./os/OSKit";
import OSSmartServiceForm from "./os/OSSmartServiceForm";
import QuoteRecord from "./os/QuoteRecord";
import QuotePublic from "./pages/QuotePublic";
import ClientRecord from "./os/ClientRecord";
import ServiceRecord from "./os/ServiceRecord";
import { NetworkVehicleRecord, NetworkDriverRecord, NetworkPartnerRecord } from "./os/NetworkRecords";
import DriverClockMobile from "./pages/DriverClockMobile";
import StaffPortalMobile from "./pages/StaffPortalMobile";
import PartnerLanding from "./pages/PartnerLanding";
import MobileAdminUnlock from "./pages/MobileAdminUnlock";
import ACEApp from "./pages/ACEApp";
import BookingSuccess from "./pages/BookingSuccess";
import RentalBooking from "./pages/RentalBooking";
import RentalRequest from "./pages/RentalRequest";
import About from "./pages/About";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AuthCallback, { AdminLogin } from "./pages/AuthCallback";
import AdminDashboard from "./pages/AdminDashboard";
import AdminConsultations from "./pages/AdminConsultations";
import AdminPartners from "./pages/AdminPartners";
import AdminContacts from "./pages/AdminContacts";
import PricingControlHQ from "./pages/PricingControlHQ";
import FleetManagerHQ from "./pages/FleetManagerHQ";
import CMSAdmin from "./pages/CMSAdmin";
import ExecutiveControlRoom from "./pages/ExecutiveControlRoom";
import ClientAuthCallback, { ClientLogin } from "./pages/ClientAuthCallback";
import ClientPortal from "./pages/ClientPortal";
import ClientBookings from "./pages/ClientBookings";
import ClientPayments from "./pages/ClientPayments";
import ClientProfile from "./pages/ClientProfile";
import ClientLoyalty from "./pages/ClientLoyalty";
import DriverTracker from "./pages/DriverTracker";
import ClientTracker from "./pages/ClientTracker";
import AdminTracking from "./pages/AdminTracking";
import AdminLoyalty from "./pages/AdminLoyalty";
import AdminDrivers from "./pages/AdminDrivers";
import AdminFleetMedia from "./pages/AdminFleetMedia";
import AdminMediaLibrary from "./pages/AdminMediaLibrary";
import AdminPhotoPicker from "./pages/AdminPhotoPicker";
import DispatchBoard from "./pages/DispatchBoard";
import CommandCenter from "./pages/CommandCenter";
import DriverRegister from "./pages/DriverRegister";
import RideTracker from "./pages/RideTracker";
import LifestyleAssessment from "./pages/LifestyleAssessment";
import VirtualTour from "./pages/VirtualTour";
import Rates from "./pages/Rates";
import ConsultingIntake from "./pages/ConsultingIntake";
import TrackDIntake from "./pages/TrackDIntake";
import AutoLifestyleQuiz from "./pages/AutoLifestyleQuiz";
import Rentals from "./pages/Rentals";
import RentalDetail from "./pages/RentalDetail";
import SalesInfo from "./pages/SalesInfo";
import AdminRentalPricing from "./pages/AdminRentalPricing";
import AdminUsers from "./pages/AdminUsers";
import AdminCRM from "./pages/AdminCRM";
import PublicContract from "./pages/PublicContract";
import TripSheet, { TripAuthCallback } from "./pages/TripSheet";
import DriverHome from "./pages/DriverHome";
import PublicInvoice from "./pages/PublicInvoice";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Layout wrapper to conditionally show header/footer
function Layout({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isPricingHQ = location.pathname === '/pricing-hq';
  const isCMSAdmin = location.pathname === '/cms-admin' || location.pathname === '/admin/cms';
  const isCommandCenter = location.pathname === '/command-center';
  const isVirtualTour = location.pathname === '/fleet/virtual-tour';
  const isPublicDoc = location.pathname.startsWith('/contracts/') || location.pathname.startsWith('/invoices/');
  const isPortalDashboard = location.pathname.startsWith('/broker/') || location.pathname.startsWith('/owners/') || location.pathname.startsWith('/collective') || location.pathname.startsWith('/os');
  const hideHeaderFooter = isAdminRoute || isPortalRoute || isPortalDashboard || isPricingHQ || isCMSAdmin || isCommandCenter || isVirtualTour || isPublicDoc || location.pathname.startsWith('/driver') || location.pathname.startsWith('/staff') || location.pathname.startsWith('/partners/') || location.pathname === '/m' || location.pathname === '/mobile' || location.pathname.startsWith('/app') || location.pathname.startsWith('/track') || location.pathname === '/drive-with-ace' || location.pathname.startsWith('/consultation/') || location.pathname === '/assessment';
  
  return (
    <>
      {!hideHeaderFooter && <Header />}
      {children}
      {!hideHeaderFooter && <Footer />}
      {!hideHeaderFooter && <FloatingChat />}
    </>
  );
}

// Main router component
function AppRouter() {
  const location = useLocation();
  
  // Handle OAuth callback for admin
  if (location.hash?.includes('session_id=') && location.pathname === '/admin') {
    return <AuthCallback />;
  }
  
  // Handle OAuth callback for the mobile app shell
  if (location.hash?.includes('session_id=') && location.pathname === '/app') {
    return <AuthCallback dest="/app" />;
  }
  
  // Handle OAuth callback for driver trip sheets & driver home
  if (location.hash?.includes('session_id=') && (location.pathname.startsWith('/trip/') || location.pathname === '/driver')) {
    return <TripAuthCallback />;
  }
  
  // Handle OAuth callback for client portal
  if (location.hash?.includes('session_id=') && location.pathname === '/portal') {
    return <ClientAuthCallback />;
  }
  
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingHome />} />
        <Route path="/lifestyle-assessment" element={<LifestyleAssessment />} />
        <Route path="/auto-lifestyle" element={<AutoLifestyleGPS />} />
        <Route path="/auto-lifestyle/route/:journeyId" element={<AutoLifestyleRoute />} />
        <Route path="/driven" element={<DrivenServices />} />
        <Route path="/driven/occasions" element={<DrivenOccasions />} />
        <Route path="/occasions/:occasionKey" element={<OccasionPage />} />
        <Route path="/fleet-rate-card" element={<FleetRateCard />} />
        <Route path="/driving" element={<DrivingServices />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/consultation" element={<Consultation />} />
        <Route path="/consultation/intake" element={<ConsultingIntake />} />
        <Route path="/consultation/track-d" element={<TrackDIntake />} />
        <Route path="/assessment" element={<AutoLifestyleQuiz />} />
        <Route path="/rentals" element={<Rentals />} />
        <Route path="/rentals/request" element={<RentalRequest />} />
        <Route path="/rentals/:rentalId" element={<RentalDetail />} />
        <Route path="/sales" element={<SalesInfo />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/corporate" element={<Corporate />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/vehicles" element={<Home />} />
        <Route path="/fleet-gallery" element={<FleetGallery />} />
        <Route path="/rates" element={<Rates />} />
        <Route path="/fleet/virtual-tour" element={<VirtualTour />} />
        <Route path="/fleet/:slug" element={<VehicleDetailPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/services" element={<Services />} />
        <Route path="/search" element={<Search />} />
        <Route path="/vehicle/:id" element={<VehicleDetail />} />
        <Route path="/booking" element={<BookingNew />} />
        <Route path="/intake/booking-request" element={<BookingRequestPage />} />
        <Route path="/intake/chauffeur-request" element={<ChauffeurRequestPage />} />
        <Route path="/intake/airport-transfer" element={<AirportTransferPage />} />
        <Route path="/intake/event-booking" element={<EventBookingPage />} />
        <Route path="/intake/owner-onboarding" element={<OwnerOnboardingPage />} />
        <Route path="/intake/broker-submission" element={<BrokerSubmissionPage />} />
        <Route path="/intake" element={<ServicesHub />} />
        <Route path="/services-hub" element={<ServicesHub />} />
        <Route path="/broker/login" element={<BrokerLoginPage />} />
        <Route path="/broker/dashboard" element={<BrokerDashboard />} />
        <Route path="/owners/login" element={<OwnerLoginPage />} />
        <Route path="/owners/dashboard" element={<OwnerDashboard />} />
        <Route path="/collective" element={<PartnerPortal />} />
        <Route path="/collective/partner" element={<PartnerPortal />} />
        <Route path="/os" element={<OSShell />} />
        <Route path="/os/kit" element={<OSKit />} />
        <Route path="/os/services/new" element={<OSSmartServiceForm />} />
        <Route path="/os/services/quotes/new" element={<OSSmartServiceForm />} />
        <Route path="/os/services/quotes/:quoteId/edit" element={<OSSmartServiceForm />} />
        <Route path="/os/services/quotes/:quoteId" element={<QuoteRecord />} />
        <Route path="/os/services/:bookingId/edit" element={<OSSmartServiceForm />} />
        <Route path="/os/services/:bookingId/brief" element={<ServiceRecord mode="brief" />} />
        <Route path="/os/services/:bookingId/recap" element={<ServiceRecord mode="recap" />} />
        <Route path="/os/services/:bookingId" element={<ServiceRecord />} />
        <Route path="/os/network/vehicles/:vehicleId" element={<NetworkVehicleRecord />} />
        <Route path="/os/network/drivers/:driverId" element={<NetworkDriverRecord />} />
        <Route path="/os/network/partners/:providerId" element={<NetworkPartnerRecord />} />
        <Route path="/os/clients/:clientId" element={<ClientRecord />} />
        <Route path="/os/:workspace" element={<OSShell />} />
        <Route path="/driver/:driverId/clock" element={<DriverClockMobile />} />
        <Route path="/staff" element={<StaffPortalMobile />} />
        <Route path="/staff/today" element={<StaffPortalMobile />} />
        <Route path="/partners/:slug" element={<PartnerLanding />} />
        <Route path="/m" element={<MobileAdminUnlock />} />
        <Route path="/mobile" element={<MobileAdminUnlock />} />
        <Route path="/app" element={<ACEApp />} />
        <Route path="/booking/rental" element={<RentalBooking />} />
        <Route path="/booking/success" element={<BookingSuccess />} />

        {/* Public Document Pages (linked via SMS) */}
        <Route path="/contracts/:contractId" element={<PublicContract />} />
        <Route path="/trip/:token" element={<TripSheet />} />
        <Route path="/driver" element={<DriverHome />} />
        <Route path="/invoices/:invoiceId" element={<PublicInvoice />} />
        <Route path="/quote/:token" element={<QuotePublic />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/crm" element={<AdminCRM />} />
        <Route path="/admin/consulting" element={<ConsultingPipeline />} />
        <Route path="/admin/consultations" element={<AdminConsultations />} />
        <Route path="/admin/partners" element={<AdminPartners />} />
        <Route path="/admin/contacts" element={<AdminContacts />} />
        <Route path="/pricing-hq" element={<FleetManagerHQ />} />
        <Route path="/admin/pricing-hq" element={<FleetManagerHQ />} />
        <Route path="/pricing-hq-legacy" element={<PricingControlHQ />} />
        <Route path="/cms-admin" element={<CMSAdmin />} />
        <Route path="/admin/cms" element={<CMSAdmin />} />
        <Route path="/admin/control-room" element={<ExecutiveControlRoom />} />
        <Route path="/admin/tracking" element={<AdminTracking />} />
        <Route path="/admin/loyalty" element={<AdminLoyalty />} />
        <Route path="/admin/drivers" element={<AdminDrivers />} />
        <Route path="/admin/fleet-media" element={<AdminFleetMedia />} />
        <Route path="/admin/media-library" element={<AdminMediaLibrary />} />
        <Route path="/admin/photo-picker" element={<AdminPhotoPicker />} />
        <Route path="/admin/rental-pricing" element={<AdminRentalPricing />} />
        <Route path="/admin/dispatch" element={<DispatchBoard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/command-center" element={<CommandCenter />} />
        <Route path="/admin/command-center" element={<CommandCenter />} />
        
        {/* Client Portal Routes */}
        <Route path="/portal/login" element={<ClientLogin />} />
        <Route path="/portal" element={<ClientPortal />} />
        <Route path="/portal/bookings" element={<ClientBookings />} />
        <Route path="/portal/payments" element={<ClientPayments />} />
        <Route path="/portal/profile" element={<ClientProfile />} />
        <Route path="/portal/rewards" element={<ClientLoyalty />} />
        <Route path="/portal/track/:bookingId" element={<ClientTracker />} />
        
        {/* Driver Route (no auth required, token-based) */}
        <Route path="/driver/:token" element={<DriverTracker />} />
        <Route path="/drive-with-ace" element={<DriverRegister />} />
        
        {/* Public Ride Tracker (shareable link) */}
        <Route path="/track/:bookingId" element={<RideTracker />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
